import * as React from 'react';
import propz from 'propz';
import { Tournament } from 'Src/models/tournament';
import {
  TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING,
  TOURNAMENT_TYPE_SERVER_TO_CLIENT_MAPPING
} from 'Src/consts/tournament';
import { formatAgesCompact, resolveAgeNamingFromContext } from 'Src/helpers/ageGroups';
import { AppUser } from 'Src/views/App/App';
import { getTournamentPublicSiteLink } from 'Src/helpers/link/link';
import 'Src/views/GenericView/AdminView/Tournaments/TournamentsHub/TournamentsHub.css';

interface Props {
  user: AppUser;
  tournament?: Tournament;
  onClose: () => void;
  onTeams: () => void;
  onEvents: () => void;
  onAthleteSchools: () => void;
}

const formatDate = (value: string): string => {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const actionIcon = (name: 'teams' | 'events' | 'schools'): React.ReactNode => {
  const paths = {
    teams:
      'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
    events:
      'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM8 14h3M8 18h6',
    schools: 'M3 21h18M5 21V9l7-5 7 5v12M9 13h.01M15 13h.01M9 17h.01M15 17h.01'
  };
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[name]} />
    </svg>
  );
};

export const HighLevelTournamentListDrawer = (props: Props): React.ReactElement | null => {
  const { tournament } = props;
  if (!tournament) return null;

  const start = new Date(tournament.startTime);
  const seasonStart = Number.isNaN(start.getTime())
    ? undefined
    : start.getMonth() >= 8
    ? start.getFullYear()
    : start.getFullYear() - 1;
  const season = typeof seasonStart === 'number' ? `${seasonStart}/${String(seasonStart + 1).slice(-2)}` : '—';
  const organiser = String(propz.get(tournament, ['school', 'name'], '') || '—');
  const sport = String(propz.get(tournament, ['sport', 'name'], '') || '—');
  const type = (TOURNAMENT_TYPE_SERVER_TO_CLIENT_MAPPING as any)[tournament.type] || tournament.type || '—';
  const venue = String(
    propz.get(tournament, ['venue', 'placeData', 'name'], propz.get(tournament, ['venue', 'postcode'], '')) || '—'
  );
  const gender = (TOURNAMENT_GENDER_SERVER_TO_CLIENT_MAPPING as any)[tournament.gender] || tournament.gender || '—';
  const ages = formatAgesCompact(tournament.ages || [], resolveAgeNamingFromContext(props.user, tournament));
  const isCompleted = new Date(tournament.endTime).getTime() < Date.now();
  const publicSiteStatus = String(propz.get(tournament, ['publicSite', 'status'], '') || '').toUpperCase();
  const publicSiteUrl =
    tournament.domain && publicSiteStatus !== 'DISABLED' ? getTournamentPublicSiteLink(tournament) : '';
  const field = (label: string, value: React.ReactNode) => (
    <div className="tnDrawerField">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );

  return (
    <React.Fragment>
      <button type="button" className="tnDrawerBackdrop" aria-label="Close" onClick={props.onClose} />
      <aside className="tnDrawer" role="dialog" aria-modal="true" aria-label="Tournament details">
        <div className="tnDrawerHead">
          <div className="tnDrawerIcon tnKind--tournament">
            <span aria-hidden="true">◆</span>
          </div>
          <div>
            <h2>{tournament.name}</h2>
            <p>
              <span>High-level tournament</span>
              <i />
              <span>{formatDate(tournament.startTime)}</span>
            </p>
          </div>
          <button type="button" aria-label="Close" onClick={props.onClose}>
            ×
          </button>
        </div>
        <div className="tnDrawerBody">
          <div className="tnDrawerContext isParticipant">
            <span aria-hidden="true">✓</span>
            <div>
              <strong>Your union is entered in this tournament.</strong>
              <p>Build teams from pupils across the union’s member schools and manage the tournament events.</p>
            </div>
          </div>
          <div className="tnDrawerSectionTitle">
            <span>Tournament details</span>
          </div>
          <div className="tnDrawerDetails">
            {field(
              'Status',
              <span className={`tnStatus ${isCompleted ? 'tnStatus--neutral' : 'tnStatus--success'}`}>
                {isCompleted ? 'Completed' : 'Entered'}
              </span>
            )}
            {field('Organiser', organiser)}
            {field('Sport', sport)}
            {field('Type', type)}
            {field('Season', season)}
            {field('Starts', formatDate(tournament.startTime))}
            {field('Venue', venue)}
            {field('Age groups & gender', [ages, gender].filter(Boolean).join(' / '))}
            {publicSiteUrl
              ? field(
                  'Public website',
                  <a className="tnPublicSiteLink" href={publicSiteUrl} target="_blank" rel="noopener noreferrer">
                    Open tournament website
                  </a>
                )
              : null}
          </div>
        </div>
        <div className="tnDrawerFoot">
          <div className="tnDrawerActions">
            <div className="tnDrawerActions__main">
              <button type="button" className="tnBtn tnBtnPrimary tnBtnWithIcon" onClick={props.onTeams}>
                {actionIcon('teams')}
                Manage teams
              </button>
              <button type="button" className="tnBtn tnBtnSecondary tnBtnWithIcon" onClick={props.onEvents}>
                {actionIcon('events')}
                Manage events
              </button>
            </div>
            <button
              type="button"
              className="tnBtn tnBtnSecondary tnBtnWithIcon hltDrawerAthleteSchools"
              onClick={props.onAthleteSchools}
            >
              {actionIcon('schools')}
              Manage represented schools
            </button>
          </div>
        </div>
      </aside>
    </React.Fragment>
  );
};
